import { Sprite, Texture } from 'pixi.js';

const cache: Record<string, Texture> = {};

function clamp(v: number, min = 0, max = 1): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Create a sprite using a cached signed-distance-field circle texture.
 * @param outerRadius Outer radius of the circle
 * @param innerRadius Inner radius for rings. 0 for a filled circle.
 */
export default function createSdfCircle(
  outerRadius: number,
  innerRadius = 0,
): Sprite {
  const key = `${outerRadius}:${innerRadius}`;
  let tex = cache[key];
  if (!tex) {
    const size = Math.ceil(outerRadius * 2);
    const data = new Uint8Array(size * size * 4);
    const aa = 1; // anti-aliasing width
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x + 0.5 - outerRadius;
        const dy = y + 0.5 - outerRadius;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const outer = clamp(0.5 - (dist - outerRadius) / aa);
        let alpha = outer;
        if (innerRadius > 0) {
          const inner = clamp(0.5 - (innerRadius - dist) / aa);
          alpha = outer * inner;
        }
        const idx = (y * size + x) * 4;
        data[idx] = 255;
        data[idx + 1] = 255;
        data[idx + 2] = 255;
        data[idx + 3] = Math.round(alpha * 255);
      }
    }
    tex = Texture.from({ resource: data, width: size, height: size });
    cache[key] = tex;
  }
  const sprite = new Sprite(tex);
  sprite.anchor.set(0.5);
  return sprite;
}
